import { ref, computed } from "vue";
import { getOperatingCountry } from "@/api";
import { mergeLocationCountryConfig } from "@components/LocationPicker/locationCountryConfigs";
import { resolveCountryFlagIcon } from "@components/utils/countryFlagIcon";
import { mapCountryPickerItem } from "@components/PayRangesListPage/composables/usePayRangesListPageState";
import { iso2FromCountryName } from "@components/PayRangeModal/composables/countryNameToIso2";

const OPERATING_PAGE_SIZE = 100;
const MAX_OPERATING_PAGES = 20;

/** Map GET /api/common/v2/country/ row → LocationPicker country config (tests + reuse). */
export function mapCatalogCountryToLocationConfig(row) {
  if (!row) return null;
  const name = String(row.name || row.country_name || "").trim();
  let code = String(row.code || row.iso2 || row.iso_code || "")
    .trim()
    .toUpperCase();
  if (code.length !== 2) {
    code = iso2FromCountryName(name) || "";
  }
  if (code.length !== 2) return null;
  const displayName = name || code;
  const catalogCountryId = row.id ?? row.pk ?? null;
  const icon = resolveCountryFlagIcon({
    code,
    flag_icon: row.flag_icon,
    flag: row.flag,
    icon: row.icon,
  });
  const merged = mergeLocationCountryConfig({
    code,
    name: displayName,
    icon,
    catalogCountryId,
    id: catalogCountryId,
  });
  return merged;
}

function mapOperatingRowsToLocationOptions(operatingItems = []) {
  return (operatingItems || [])
    .map((row) => {
      const fromApi = mapCountryPickerItem(row);
      const code = String(
        row?.code || fromApi?.code || iso2FromCountryName(row?.name || fromApi?.name) || ""
      )
        .trim()
        .toUpperCase();
      const resolvedCode = String(fromApi?.code || code).trim().toUpperCase();
      if (resolvedCode.length !== 2) return null;
      const base =
        fromApi ||
        {
          id: row.id ?? null,
          code: resolvedCode,
          name: row.name || code,
          icon:
            row.icon ||
            resolveCountryFlagIcon({
              code,
              flag_icon: row.flag_icon,
              flag: row.flag,
            }),
        };
      const operatingCountryId = row.operatingCountryId ?? row.id ?? base.id ?? null;
      return mergeLocationCountryConfig({
        ...base,
        ...row,
        code: resolvedCode,
        ...(operatingCountryId != null
          ? { operatingCountryId, id: operatingCountryId }
          : {}),
      });
    })
    .filter(Boolean);
}

function sortByName(list) {
  return [...list].sort((a, b) => String(a.name).localeCompare(String(b.name)));
}

/**
 * Operating countries for the Pay Range Location picker — same scope as Used Currency
 * (account operating rows only, not the global country catalog).
 *
 * @param {() => Array} getOperatingCountryItems — PayRangeModal `countryItems` prop
 * @param {() => string|number|null} [getAccountId]
 */
export function usePayRangeCountryCatalog(getOperatingCountryItems, getAccountId) {
  const accountOperatingRows = ref(null);
  const accountOperatingLoading = ref(false);

  async function loadAccountOperatingCountries() {
    const fromParent = getOperatingCountryItems?.() || [];
    if (fromParent.length) {
      accountOperatingRows.value = fromParent;
      return;
    }

    const accountId = getAccountId?.();
    if (!accountId) return;

    if (accountOperatingRows.value?.length) return;

    accountOperatingLoading.value = true;
    try {
      const collected = [];
      let offset = 0;
      let total = null;

      for (let page = 0; page < MAX_OPERATING_PAGES; page += 1) {
        const response = await getOperatingCountry(accountId, {
          offset,
          size: OPERATING_PAGE_SIZE,
          status: ["active"],
        });
        const data = response?.data;
        if (total === null) {
          const count = Number(data?.count);
          total = Number.isFinite(count) ? count : null;
        }
        const rows = Array.isArray(data?.results) ? data.results : [];
        collected.push(...rows);
        offset += rows.length;
        if (!rows.length) break;
        if (total !== null && offset >= total) break;
      }

      accountOperatingRows.value = collected;
    } catch {
      accountOperatingRows.value = null;
    } finally {
      accountOperatingLoading.value = false;
    }
  }

  async function loadCatalog() {
    await loadAccountOperatingCountries();
  }

  const locationCountryOptions = computed(() => {
    const parentOperating = getOperatingCountryItems?.() || [];
    const fetchedOperating = accountOperatingRows.value || [];
    const operatingSource =
      parentOperating.length > 0 ? parentOperating : fetchedOperating;

    if (!operatingSource.length) {
      if (accountOperatingLoading.value) return [];
      return [];
    }

    return sortByName(mapOperatingRowsToLocationOptions(operatingSource));
  });

  const catalogLoading = computed(() => accountOperatingLoading.value);

  return {
    catalogLoading,
    loadCatalog,
    locationCountryOptions,
  };
}
